<template>
  <div>
    <slot name="actions" :onOpenCheckout="onOpenCheckout" />
    <CheckoutModal
      v-if="showCheckoutModal"
      v-model="isModalOpen"
      :title="title"
      :checkout="selectedCheckout"
      :operation="operation"
      :items="checkouts.data"
      :store-id="selectStoreForOperations"
      :is-admin="isAdmin"
      :has-active-products="hasActiveProducts"
      @ok="manageCheckout"
    />

    <OperationsModal
      v-if="showOperationsModal"
      v-model="isOperationsModalOpen"
      :checkout="selectedCheckout"
      :type="operationTypes"
      @close="closeOperationsModal"
    />

    <CheckoutPhotosModal
      v-if="showPhotosModal"
      v-model="isPhotosModalOpen"
      :checkout="selectedCheckout"
      :can-manage="isAdmin"
      @refresh="$emit('refresh')"
    />

    <template v-if="!hideWarnings">
      <b-alert v-if="checkouts.error" show variant="danger">
        <span class="fas fa-exclamation-triangle mr-1" /> {{ checkouts.error }}
      </b-alert>

      <b-alert v-if="hasOpenCheckout" show variant="warning">
        <span class="fas fa-exclamation-triangle mr-1" /> Para poder abrir um
        novo caixa é preciso fechar o caixa atual.
      </b-alert>
    </template>

    <b-table
      ref="checkoutsList"
      :fields="fields"
      :items="checkouts.data"
      striped
      hover
      stacked="md"
      show-empty
      :busy="checkouts.loading"
    >
      <div slot="empty" class="text-center">Nenhum caixa registrado</div>
      <template #table-colgroup="scope">
        <col
          v-for="field in scope.fields"
          :key="field.key"
          :style="getColumnStyle(field.key)"
        />
      </template>
      <template #cell(opening)="{ item }">
        <div>
          <span class="d-block">
            <b>{{ item.createdAt | formatDateTime }}</b>
          </span>
          <span class="d-block">
            <b>Valor inicial:</b> {{ item.openingValue | formatCurrency }}
          </span>
          <small v-if="item.openingOverrideReason" class="d-block text-warning">
            <span class="fas fa-user-shield mr-1" />Abertura ajustada por
            administrador
          </small>
          <small v-if="item.openingOverrideReason" class="d-block text-muted">
            <b>Motivo:</b> {{ item.openingOverrideReason }}
          </small>
          <span v-if="hasActiveProducts" class="d-block">
            <b>Qtde Produtos:</b> {{ item.openingProductCount }}
          </span>
          <small class="text-muted">
            <b>Aberto por:</b> {{ item.createdByUser.name }}
          </small>
        </div>
      </template>
      <template #cell(closing)="{ item }">
        <div v-if="item.closedAt">
          <span class="d-block">
            <b>{{ item.closedAt | formatDateTime }}</b>
          </span>
          <span class="d-block">
            <b>Valor final:</b> {{ item.closingValue | formatCurrency }}
          </span>
          <span
            v-if="hasClosingDifference(item)"
            class="d-block"
            :class="closingDifferenceClass(item)"
          >
            <b>{{ closingDifferenceLabel(item) }}:</b>
            {{ closingDifferenceAmount(item) | formatCurrency }}
          </span>
          <small v-if="hasClosingDifference(item)" class="d-block text-muted">
            <b>Esperado:</b>
            {{ Number(item.closingExpectedValue) | formatCurrency }}
          </small>
          <small v-if="item.closingDifferenceReason" class="d-block text-muted">
            <b>Motivo:</b> {{ item.closingDifferenceReason }}
          </small>
          <span v-if="hasActiveProducts" class="d-block">
            <b>Qtde Produtos:</b> {{ item.closingProductCount }}
          </span>
          <small v-if="item.closedByUser" class="text-muted">
            <b>Fechado por:</b> {{ item.closedByUser.name }}
          </small>
        </div>
      </template>
      <template #cell(status)="{ item }">
        <Status :value="getStatus(item)" />
      </template>
      <template #cell(actions)="{ item }">
        <div class="checkout-actions d-flex flex-wrap gap-1">
          <b-button
            size="sm"
            class="d-flex align-items-center justify-content-center"
            variant="dark"
            @click="openCheckoutOperationsModal(item)"
          >
            <span class="fas fa-eye mr-1" />
            <span> Lançamentos </span>
          </b-button>
          <b-button
            v-if="canCloseCheckout(item)"
            size="sm"
            class="d-flex align-items-center justify-content-center"
            variant="primary"
            @click="onCloseCheckout(item)"
          >
            <span class="fas fa-calculator mr-1" />
            <span> Fechar </span>
          </b-button>
          <b-button
            v-if="isReport"
            size="sm"
            class="d-flex align-items-center justify-content-center"
            variant="primary"
            @click="onViewCheckout(item)"
          >
            <span class="fas fa-eye mr-1" />
            <span> Ver Detalhes </span>
          </b-button>
          <b-button
            v-if="isReport"
            size="sm"
            class="d-flex align-items-center justify-content-center"
            variant="outline-primary"
            @click="openCheckoutPhotosModal(item)"
          >
            <span class="fas fa-images mr-1" />
            <span> Fotos </span>
          </b-button>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import {
  CHECKOUT_OPERATIONS,
  CHECKOUT_STATUS,
  OPERATION_TYPES
} from '@/constants'

import { isSameDay } from 'date-fns'
import services from '@/services'
import { formatCurrency } from '@/helpers/numbers'
import { parseCheckout } from '@/helpers/checkouts'
import { formatDate, formatDateTime } from '@/helpers/date'

import Status from '@/components/Status'
import CheckoutModal from './CheckoutModal'
import OperationsModal from './OperationsModal'
import CheckoutPhotosModal from './CheckoutPhotosModal'

export default {
  name: 'CheckoutsList',
  filters: {
    formatCurrency,
    formatDate,
    formatDateTime
  },
  components: {
    Status,
    CheckoutModal,
    OperationsModal,
    CheckoutPhotosModal
  },
  props: {
    checkouts: {
      type: Object,
      required: true
    },
    hideWarnings: {
      type: Boolean
    },
    isReport: {
      type: Boolean
    },
    readonly: {
      type: Boolean
    },
    isAdmin: {
      type: Boolean
    },
    hasActiveProducts: {
      type: Boolean,
      default: true
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      title: '',
      operation: '',
      isModalOpen: false,
      isBusy: false,
      error: null,
      isOperationsModalOpen: false,
      isPhotosModalOpen: false,
      selectedCheckout: null,
      selectStoreForOperations: null,
      filter: {
        storeId: null,
        date: null
      }
    }
  },
  computed: {
    fields() {
      return [
        {
          key: 'opening',
          label: 'Abertura'
        },
        ...(this.hasClosedCheckout
          ? [
              {
                key: 'closing',
                label: 'Fechamento'
              }
            ]
          : []),
        ...(!this.compact
          ? [
              {
                key: 'status',
                label: 'Status'
              }
            ]
          : []),
        {
          key: 'actions',
          label: 'Ações'
        }
      ]
    },
    filteredCheckouts() {
      return this.checkouts?.data.filter(({ closedAt }) => !closedAt)
    },
    hasOpenCheckout() {
      return this.checkouts?.data.some(({ closedAt }) => !closedAt)
    },
    hasClosedCheckout() {
      return this.checkouts?.data.some(({ closedAt }) => Boolean(closedAt))
    },
    hasOldCheckout() {
      isSameDay
      return false
      // return this.checkouts.some(
      //   ({ createdAt, closedAt }) =>
      //     !isSameDay(new Date(createdAt), new Date()) || !closedAt
      // )
    },
    showOperationsModal() {
      return (
        this.selectedCheckout &&
        this.operation === CHECKOUT_OPERATIONS.OPERATIONS
      )
    },
    showPhotosModal() {
      return this.selectedCheckout && this.operation === 'photos'
    },
    showCheckoutModal() {
      const { OPEN, CLOSE } = CHECKOUT_OPERATIONS

      const isOpeningCheckout = this.operation === OPEN
      const isClosingCheckout =
        this.selectedCheckout && this.operation === CLOSE

      return isOpeningCheckout || isClosingCheckout
    },
    operationTypes() {
      return Object.values(OPERATION_TYPES)
    }
  },
  methods: {
    getColumnStyle(field) {
      return { width: field === 'actions' ? '25%' : '' }
    },
    getStatus(checkout) {
      return checkout.closedAt
        ? CHECKOUT_STATUS.CLOSED.value
        : CHECKOUT_STATUS.OPEN.value
    },
    hasClosingDifference(checkout) {
      return Number(checkout.closingDifference || 0) !== 0
    },
    closingDifferenceClass(checkout) {
      return Number(checkout.closingDifference) < 0
        ? 'text-danger'
        : 'text-success'
    },
    closingDifferenceLabel(checkout) {
      return Number(checkout.closingDifference) < 0 ? 'Falta' : 'Sobra'
    },
    closingDifferenceAmount(checkout) {
      return Math.abs(Number(checkout.closingDifference))
    },
    async manageCheckout(checkout) {
      if (this.operation === CHECKOUT_OPERATIONS.OPEN) {
        await this.openCheckout(checkout)
      } else {
        await this.closeCheckout(checkout)
      }
    },
    async openCheckout(checkout) {
      const checkoutId = this.checkouts.data[0]?.id || null
      const storeId = this.selectStoreForOperations || null
      await services.checkouts.openCheckout({
        checkoutId,
        storeId,
        ...parseCheckout({ ...this.selectedCheckout }),
        ...parseCheckout(checkout)
      })

      this.isModalOpen = false
      this.$root.$toast.success('Caixa aberto com sucesso')
      this.$emit('refresh')
    },
    async closeCheckout(checkout) {
      await services.checkouts.closeCheckout({
        ...this.selectedCheckout,
        ...parseCheckout(checkout)
      })

      this.isModalOpen = false
      this.selectedCheckout = null
      this.$root.$toast.success('Caixa fechado com sucesso')
      this.$emit('refresh')
    },
    onOpenCheckout(storeId) {
      this.title = 'Abrir Novo Caixa'
      this.operation = CHECKOUT_OPERATIONS.OPEN
      this.selectedCheckout = null
      this.isModalOpen = true
      this.selectStoreForOperations = storeId || null
    },
    onCloseCheckout(checkout) {
      this.title = 'Fechar Caixa'
      this.operation = CHECKOUT_OPERATIONS.CLOSE
      this.selectedCheckout = checkout
      this.isModalOpen = true
    },
    onViewCheckout(checkout) {
      this.$router.push({
        name: 'ReportCheckoutDetails',
        params: { id: checkout.id }
      })
    },
    openCheckoutOperationsModal(checkout) {
      this.selectedCheckout = checkout
      this.operation = CHECKOUT_OPERATIONS.OPERATIONS
      this.$nextTick(() => (this.isOperationsModalOpen = true))
    },
    openCheckoutPhotosModal(checkout) {
      this.selectedCheckout = checkout
      this.operation = 'photos'
      this.$nextTick(() => (this.isPhotosModalOpen = true))
    },
    closeOperationsModal() {
      this.selectedCheckout = null
      this.operation = null
    },
    canCloseCheckout(item) {
      return !item.closedAt && !this.isReport && !this.readonly
    }
  }
}
</script>

<style scoped>
.checkout-actions {
  min-width: 0;
}

@media (max-width: 767.98px) {
  .checkout-actions .btn {
    flex: 1 1 auto;
  }
}
</style>
